// Panneau « Paramètres de l'événement » (colonne de droite de l'écran de config) : jours de
// l'événement et types d'accès, partagés par tous les formulaires. Édition directe, enregistrée
// automatiquement par le parent (cf. useAutosave) — pas de bouton « Enregistrer ».
import React, { useState } from 'react';
import { Box, Stack, Typography, TextField, IconButton, Button, Alert, CircularProgress } from '@mui/material';
import { CalendarDays, Tag, Plus, X, Sparkles, Check } from 'lucide-react';

const MONTHS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const weekdayOf = (iso) => {
  const label = new Date(`${iso}T12:00`).toLocaleDateString('fr-FR', { weekday: 'long' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const STATUS_VIEW = {
  idle: { label: 'Enregistrement automatique', color: '#5e646a' },
  saving: { label: 'Enregistrement…', color: '#5e646a' },
  saved: { label: 'Enregistré', color: '#007739' },
  error: { label: 'Non enregistré', color: '#b42318' },
};

const sectionSx = { display: 'flex', flexDirection: 'column', gap: 1.5, p: 2.25 };
const dashedInputSx = {
  '& .MuiInputBase-root': { height: 36, fontSize: 13, borderRadius: '6px' },
  '& fieldset': { borderStyle: 'dashed', borderColor: '#c7d0d7' },
};

const AccreditationSettingsAside = ({ days, onDaysChange, types, onTypesChange, importCount, onImportNames, status, error }) => {
  const [newDate, setNewDate] = useState('');
  const [newType, setNewType] = useState('');
  const view = STATUS_VIEW[status] || STATUS_VIEW.idle;

  const addDay = () => {
    if (!newDate || days.some((d) => d.date === newDate)) return;
    const next = [...days, { date: newDate, label: weekdayOf(newDate) }].sort((a, b) => a.date.localeCompare(b.date));
    onDaysChange(next);
    setNewDate('');
  };

  const addType = () => {
    const label = newType.trim();
    if (!label || types.some((t) => t.label.trim().toLowerCase() === label.toLowerCase())) return;
    onTypesChange([...types, { label }]);
    setNewType('');
  };

  return (
    <Box component="aside" aria-label="Paramètres de l'événement" sx={{
      flex: '0 1 340px', minWidth: 300, position: { md: 'sticky' }, top: 24,
      bgcolor: '#fff', border: '1px solid #dde3e8', borderRadius: '12px',
    }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2.25, py: 2, borderBottom: '1px solid #e9edf1' }}>
        <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 700 }}>Paramètres de l'événement</Typography>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: view.color, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {status === 'saving' && <CircularProgress size={11} color="inherit" />}
          {status === 'saved' && <Check size={13} />}
          <span>{view.label}</span>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ m: 2, mb: 0 }}>{error}</Alert>}

      <Box sx={sectionSx}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ fontSize: 14, fontWeight: 600 }}>
            <CalendarDays size={15} color="#00876f" /><span>Jours</span>
          </Stack>
          <Typography sx={{ fontSize: 12, lineHeight: 1.45, color: '#5e646a' }}>Proposés dans chaque formulaire.</Typography>
        </Box>
        <Stack spacing={0.75}>
          {days.map((d, idx) => {
            const dt = d.date ? new Date(`${d.date}T12:00`) : null;
            const valid = dt && !Number.isNaN(dt.getTime());
            return (
              <Stack key={d.date || idx} direction="row" alignItems="center" spacing={1.5} sx={{ p: 0.75, border: '1px solid #e9edf1', borderRadius: '8px' }}>
                <Stack alignItems="center" sx={{ width: 40, flexShrink: 0, py: 0.4, borderRadius: '6px', bgcolor: '#eef3f6' }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#5e646a', lineHeight: 1.2 }}>
                    {valid ? MONTHS[dt.getMonth()].replace('.', '') : '—'}
                  </Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.1 }}>{valid ? dt.getDate() : ''}</Typography>
                </Stack>
                <TextField
                  size="small" variant="standard" value={d.label} fullWidth
                  onChange={(e) => onDaysChange(days.map((x, i) => (i === idx ? { ...x, label: e.target.value } : x)))}
                  InputProps={{ disableUnderline: true, sx: { fontSize: 14, fontWeight: 500, px: 1 } }}
                />
                <IconButton size="small" aria-label="Retirer ce jour" onClick={() => onDaysChange(days.filter((_, i) => i !== idx))}
                  sx={{ color: '#9aa3aa', '&:hover': { bgcolor: '#fdecea', color: '#b42318' } }}>
                  <X size={13} />
                </IconButton>
              </Stack>
            );
          })}
        </Stack>
        <Stack direction="row" spacing={0.75}>
          <TextField
            size="small" type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} fullWidth sx={dashedInputSx}
            inputProps={{ 'aria-label': 'Date du nouveau jour' }}
          />
          <Button onClick={addDay} disabled={!newDate} startIcon={<Plus size={12} />}
            sx={{ height: 36, bgcolor: '#eef3f6', color: '#002f36', fontWeight: 600, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#e2eaef' } }}>
            Ajouter
          </Button>
        </Stack>
      </Box>

      <Box sx={{ ...sectionSx, borderTop: '1px solid #e9edf1' }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ fontSize: 14, fontWeight: 600 }}>
            <Tag size={15} color="#00876f" /><span>Types d'accès</span>
            <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: '#5e646a' }}>{types.length}</Typography>
          </Stack>
          <Typography sx={{ fontSize: 12, lineHeight: 1.45, color: '#5e646a' }}>
            Choisis par les chefs de pôle pour chaque personne. Deviennent les noms de tarif à l'export Wilout.
          </Typography>
        </Box>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
          {types.map((t, idx) => (
            <Stack key={t.key || `new-${idx}`} direction="row" alignItems="center" spacing={0.25}
              sx={{ height: 30, pl: 1.25, pr: 0.5, bgcolor: '#eef3f6', borderRadius: '8px', maxWidth: '100%' }}>
              <Typography noWrap sx={{ fontSize: 12, fontWeight: 600 }}>{t.label}</Typography>
              <IconButton size="small" aria-label={`Retirer ${t.label}`} onClick={() => onTypesChange(types.filter((_, i) => i !== idx))}
                sx={{ p: 0.4, color: '#9aa3aa', '&:hover': { bgcolor: '#fdecea', color: '#b42318' } }}>
                <X size={11} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        <Stack direction="row" spacing={0.75}>
          <TextField
            size="small" fullWidth value={newType} placeholder="Nouveau type, puis Entrée" sx={dashedInputSx}
            onChange={(e) => setNewType(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addType(); } }}
          />
          <Button onClick={addType} disabled={!newType.trim()} startIcon={<Plus size={12} />}
            sx={{ height: 36, bgcolor: '#eef3f6', color: '#002f36', fontWeight: 600, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#e2eaef' } }}>
            Ajouter
          </Button>
        </Stack>
        <Button onClick={onImportNames} disabled={importCount === 0} startIcon={<Sparkles size={13} />}
          sx={{ alignSelf: 'flex-start', p: 0, color: '#00876f', fontWeight: 600, '&:hover': { bgcolor: 'transparent', color: '#002f36' } }}>
          Reprendre les noms des formulaires
        </Button>
      </Box>
    </Box>
  );
};

export default AccreditationSettingsAside;
